import { useState, useEffect } from "react";

/**
 * Types out a sequence of terminal lines.
 * lines: array of { type: "cmd" | "out", text }
 *  - "cmd" lines are typed character-by-character behind a `$` prompt
 *  - "out" lines appear instantly (command output)
 */
export default function Typewriter({
  lines = [],
  speed = 30,
  startDelay = 350,
  className = "",
}) {
  const [done, setDone] = useState(0);
  const [typed, setTyped] = useState("");
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let li = 0;
    let ci = 0;

    const run = () => {
      if (cancelled) return;
      if (li >= lines.length) {
        setFinished(true);
        return;
      }
      const line = lines[li];
      if (line.type === "out") {
        li += 1;
        setDone(li);
        timer = setTimeout(run, 320);
        return;
      }
      // command: type char by char
      if (ci <= line.text.length) {
        setTyped(line.text.slice(0, ci));
        ci += 1;
        timer = setTimeout(run, speed);
      } else {
        li += 1;
        ci = 0;
        setDone(li);
        setTyped("");
        timer = setTimeout(run, 280);
      }
    };

    timer = setTimeout(run, startDelay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lines, speed, startDelay]);

  const renderLine = (line, i, partial, showCursor) => {
    const content = partial !== undefined ? partial : line.text;
    if (line.type === "cmd") {
      return (
        <div key={i} className="leading-relaxed break-words">
          <span className="font-bold">$</span> {content}
          {showCursor && <span className="blink">&nbsp;</span>}
        </div>
      );
    }
    return (
      <div key={i} className="leading-relaxed break-words text-black/70 pl-3">
        {content}
      </div>
    );
  };

  return (
    <div className={`text-sm sm:text-base ${className}`}>
      {lines.slice(0, done).map((l, i) => renderLine(l, i))}
      {done < lines.length &&
        lines[done].type === "cmd" &&
        renderLine(lines[done], done, typed, true)}
      {finished && (
        <div className="leading-relaxed">
          <span className="font-bold">$</span> <span className="blink">&nbsp;</span>
        </div>
      )}
    </div>
  );
}
